'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import nProgress from 'nprogress';

/**
 * Global route progress indicator
 * Automatically handles nProgress for route changes with zero business code intrusion
 *
 * How it works:
 * - Uses Next.js router and pathname to track navigation
 * - Starts progress on link clicks
 * - Completes progress when pathname changes
 * - Supports both programmatic navigation and link clicks
 */
export function RouteProgress() {
  const router = useRouter();

  // Configure nProgress and set up navigation interceptors
  useEffect(() => {
    // Configure nProgress
    nProgress.configure({
      showSpinner: false,
      trickleSpeed: 100,
      minimum: 0.1,
    });

    // Store original router methods
    const originalPush = router.push;
    const originalReplace = router.replace;
    const originalBack = router.back;
    const originalForward = router.forward;

    // Wrap router.push
    router.push = function (...args: Parameters<typeof originalPush>) {
      if (!nProgress.isStarted()) {
        nProgress.start()
      }
      return originalPush.apply(this, args);
    };

    // Wrap router.replace
    router.replace = function (...args: Parameters<typeof originalReplace>) {
      if (!nProgress.isStarted()) {
        nProgress.start()
      }
      return originalReplace.apply(this, args);
    };

    // Wrap router.back
    router.back = function () {
      if (!nProgress.isStarted()) {
        nProgress.start()
      }
      return originalBack.call(this);
    };

    // Wrap router.forward
    router.forward = function () {
      if (!nProgress.isStarted()) {
        nProgress.start()
      }
      return originalForward.call(this);
    };

    // Cleanup
    return () => {
      // Restore original methods
      router.push = originalPush;
      router.replace = originalReplace;
      router.back = originalBack;
      router.forward = originalForward;
    };
  }, [router]);

  return null;
}
